import React, { FC } from 'react';
import { Box, Skeleton } from '@mui/material';

interface LoadingStateV2Props {
  isPreviewMode?: boolean;
}

export const LoadingStateV2: FC<LoadingStateV2Props> = ({
  isPreviewMode = false,
}) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        width: '100%',
        maxWidth: '1200px',
        height: '700px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header Skeleton */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '64px',
        }}
      >
        <Skeleton variant="text" width={200} height={24} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>

      {/* Main Content Skeleton */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Left side - Image skeleton */}
        <Box sx={{ width: '50%', p: 3 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{ borderRadius: 1 }}
          />
        </Box>

        {/* Right side - Content skeleton */}
        <Box
          sx={{
            width: '50%',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'auto',
          }}
        >
          {/* Title */}
          <Skeleton variant="text" width="90%" height={32} />

          {/* Tags */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={60} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={70} height={24} />
          </Box>

          {/* Description */}
          <Box sx={{ mt: 1 }}>
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="95%" height={20} />
            <Skeleton variant="text" width="85%" height={20} />
            <Skeleton variant="text" width="75%" height={20} />
          </Box>

          {/* Protein dropdown section */}
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={56}
              sx={{ borderRadius: 1 }}
            />
          </Box>

          {/* Ingredients section */}
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={18} />
            <Skeleton variant="text" width="90%" height={18} />
            <Skeleton variant="text" width="85%" height={18} />
          </Box>

          {/* Nutritional info section */}
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width={140} height={20} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Skeleton variant="text" width={80} height={18} />
              <Skeleton variant="text" width={90} height={18} />
              <Skeleton variant="text" width={70} height={18} />
              <Skeleton variant="text" width={85} height={18} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer Skeleton - Only show if not in preview mode */}
      {!isPreviewMode && (
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '80px',
          }}
        >
          <Skeleton variant="text" width={180} height={20} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={40}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              variant="rectangular"
              width={180}
              height={48}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoadingStateV2;
